import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const IMG_MAIN =
  "https://images.unsplash.com/photo-1758520145147-c30bc656f314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxkZXNpZ25lciUyMHRlYW0lMjBkYXJrJTIwb2ZmaWNlfGVufDB8fHx8MTc4ODczMzYxNXww&ixlib=rb-4.1.0&q=85";
const IMG_CODE =
  "https://images.unsplash.com/photo-1489875347897-49f64b51c1f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxjb2RlJTIwc2NyZWVuJTIwY2xvc2UlMjB1cCUyMGRhcmt8ZW58MHx8fHwxNzg4NzMzNjE1fDA&ixlib=rb-4.1.0&q=85";
const IMG_SPACE =
  "https://images.unsplash.com/photo-1622126807280-9b5b32b28e77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHxkYXJrJTIwbW9kZXJuJTIwb2ZmaWNlJTIwd29ya3NwYWNlfGVufDB8fHx8MTc4ODczMzYwNXww&ixlib=rb-4.1.0&q=85";

const points = [
  "Unsere Designs sind einzigartig",
  "Durch langjährige Coding-Erfahrung und die Hilfe von KI erschaffen wir Webseiten, die von Grund auf einzigartig sind",
  "Kunden, die wir beim Namen kennen",
];

export default function Atelier() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const yCode = useTransform(scrollYProgress, [0, 1], [130, -80]);
  const ySpace = useTransform(scrollYProgress, [0, 1], [170, -150]);

  return (
    <section
      id="atelier"
      ref={ref}
      data-testid="atelier-section"
      className="relative z-10 overflow-hidden px-6 py-28 md:px-14 md:py-40"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-16 md:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="md:col-span-5"
        >
          <p className="mb-6 text-[11px] tracking-[0.45em] text-[#D4AF37] md:text-sm">
            SZENE · WEBDESIGN AUS AUGSBURG
          </p>
          <h2 className="font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl">
            Websites aus Augsburg.{" "}
            <em className="gold-text font-serif-accent normal-case italic">Echte Handarbeit.</em>
          </h2>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-[#BDBDB6] md:text-lg">
            Hinter jeder S&F Website stehen Menschen, die ihr Handwerk ernst nehmen.
            Maßgeschneidert statt Baukasten, ohne Abkürzungen — Design, Programmierung und
            Strategie unter einem Dach in Augsburg.
          </p>
          <ul className="mt-8 space-y-4">
            {points.map((p, i) => (
              <motion.li
                key={p}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.15, duration: 0.7 }}
                className="flex items-start gap-3 text-base font-light leading-relaxed text-[#F5F5F0]"
              >
                <span className="mt-[0.5em] h-1.5 w-1.5 shrink-0 rounded-full bg-[#D4AF37]" />
                {p}
              </motion.li>
            ))}
          </ul>
        </motion.div>

        <div className="relative h-[68vh] min-h-[440px] md:col-span-7">
          <motion.div
            style={{ y: yMain }}
            className="absolute left-0 top-0 h-[62%] w-[74%] overflow-hidden rounded-2xl border border-white/10"
          >
            <motion.img
              src={IMG_MAIN}
              alt="Designer bei der Arbeit im abgedunkelten Studio"
              loading="lazy"
              initial={{ clipPath: "inset(20% 20% 20% 20% round 20px)", scale: 1.15 }}
              whileInView={{ clipPath: "inset(0% 0% 0% 0% round 16px)", scale: 1 }}
              viewport={{ once: true, margin: "-15%" }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="h-full w-full object-cover"
              data-testid="atelier-image-main"
            />
          </motion.div>

          <motion.div
            style={{ y: yCode }}
            className="absolute bottom-[16%] right-0 h-[46%] w-[46%] overflow-hidden rounded-xl border border-[#D4AF37]/25 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
          >
            <motion.img
              src={IMG_CODE}
              alt="Code auf einem Bildschirm"
              loading="lazy"
              initial={{ clipPath: "inset(0% 100% 0% 0%)" }}
              whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
              viewport={{ once: true, margin: "-15%" }}
              transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="h-full w-full object-cover"
              data-testid="atelier-image-code"
            />
          </motion.div>

          <motion.div
            style={{ y: ySpace }}
            className="absolute -bottom-2 left-[8%] h-[30%] w-[38%] overflow-hidden rounded-xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
          >
            <motion.img
              src={IMG_SPACE}
              alt="Moderner dunkler Arbeitsplatz"
              loading="lazy"
              initial={{ opacity: 0, scale: 0.85 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-15%" }}
              transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="h-full w-full object-cover"
              data-testid="atelier-image-space"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7, duration: 0.9 }}
            className="absolute right-[6%] top-[6%] animate-[floaty_6s_ease-in-out_infinite] rounded-full border border-[#D4AF37]/30 bg-[#121212]/70 px-5 py-2.5 backdrop-blur-xl"
            data-testid="atelier-chip"
          >
            <span className="text-[11px] tracking-[0.25em] text-[#FFE9A8]">
              DESIGN · CODE · STRATEGIE
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
